import React, { useEffect, useState } from "react";
import { Layers, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString() : "");

export default function DuplicatesDialog({ open, onOpenChange, onMerged }) {
  const [groups, setGroups] = useState(null);
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    if (!open) return;
    setGroups(null);
    api.get("/vault/duplicates")
      .then(({ data }) => setGroups(data))
      .catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setGroups([]); });
  }, [open]);

  const extras = (groups || []).reduce((n, g) => n + g.length - 1, 0);

  const merge = async () => {
    setMerging(true);
    try {
      const { data } = await api.post("/vault/duplicates/merge");
      toast.success(`Merged ${data.groups_merged} group${data.groups_merged === 1 ? "" : "s"}, removed ${data.items_removed} duplicate${data.items_removed === 1 ? "" : "s"}`);
      onOpenChange(false);
      onMerged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Merge failed");
    } finally {
      setMerging(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] sm:max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden bg-white rounded-2xl [&>*]:min-w-0" data-testid="duplicates-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] text-xl">Find duplicates</DialogTitle>
          <DialogDescription>
            Logins with the same website and username are merged. The most recently updated password is kept, notes and tags are combined, and sharing moves to the kept item.
          </DialogDescription>
        </DialogHeader>

        {groups === null ? (
          <p className="text-sm text-[color:var(--vault-text-tertiary)] py-6 text-center" data-testid="duplicates-loading">Scanning your vault…</p>
        ) : groups.length === 0 ? (
          <div className="flex flex-col items-center py-6 text-center" data-testid="duplicates-empty">
            <CheckCircle2 size={28} className="text-[color:var(--vault-success)]" />
            <p className="font-[Outfit] font-medium mt-2">No duplicates found</p>
            <p className="text-sm text-[color:var(--vault-text-tertiary)]">Your vault is tidy.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="duplicates-list">
            <p className="text-sm" data-testid="duplicates-count">
              <span className="font-semibold">{groups.length}</span> group{groups.length === 1 ? "" : "s"} · <span className="font-semibold">{extras}</span> extra cop{extras === 1 ? "y" : "ies"} to remove
            </p>
            <div className="max-h-72 overflow-y-auto space-y-2">
              {groups.map((g, gi) => (
                <div key={g[0].id} className="rounded-xl border border-[color:var(--vault-border)] p-3" data-testid={`duplicate-group-${gi}`}>
                  <div className="text-xs text-[color:var(--vault-text-tertiary)] truncate">{g[0].url || g[0].title} · {g[0].username || "no username"}</div>
                  {g.map((it, i) => (
                    <div key={it.id} className="flex items-center gap-2 mt-1.5 min-w-0">
                      <span className="text-sm font-medium truncate flex-1">{it.title}</span>
                      <span className="text-[11px] text-[color:var(--vault-text-tertiary)] shrink-0">{fmt(it.updated_at)}</span>
                      <span className={`pill shrink-0 !text-[10px] !px-2 !py-0.5 ${i === 0 ? "text-[color:var(--vault-success)] bg-[color:var(--vault-success-bg)]" : "text-[color:var(--vault-text-tertiary)] bg-[color:var(--vault-input-bg)]"}`}>
                        {i === 0 ? "Keep" : "Merge"}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" className="rounded-xl" onClick={() => onOpenChange(false)} data-testid="duplicates-close-btn">Close</Button>
          {groups?.length > 0 && (
            <Button type="button" disabled={merging} onClick={merge} className="rounded-xl bg-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-hover)] text-white" data-testid="duplicates-merge-btn">
              <Layers size={16} className="mr-1.5" /> {merging ? "Merging…" : `Merge all (${extras})`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
